import { Component, OnInit, OnDestroy, HostListener, ElementRef, ViewChild } from '@angular/core';
import { OcrApiService } from '../../../services/pr-corp-template.service';
import {
  OcrData,
  OcrBox,
  Transform,
  LineItem,
  LineItemSelections,
  TemplateStructure,
  TransformedCoordinates,
  SearchBox,
  Coordinate
} from '../../../models/ocr.models';

@Component({
    selector: 'app-pr-corp-template-editor',
    templateUrl: './pr-corp-template-editor.component.html',
    styleUrls: ['./pr-corp-template-editor.component.scss'],
    standalone: false
})
export class PrCorpTemplateEditorComponent implements OnInit, OnDestroy {
  @ViewChild('imageContainer', { static: false }) imageContainer?: ElementRef<HTMLDivElement>;

  // Data
  ocrData: OcrData | null = null;
  currentImage: string | null = null;
  templateStructure: TemplateStructure = {};
  existingLineItems: LineItem[] = [];

  // Transform and display settings
  transform: Transform = { scaleX: 0.5, scaleY: 0.5, offsetX: 0, offsetY: 0 };
  imageWidth = 0;
  imageHeight = 0;
  currentZoom = 1.0;
  panOffset = { x: 0, y: 0 };

  // UI state
  boxOpacity = 0.8;
  boxLimit = 50;
  floatingCoordsEnabled = false;
  hoverTooltipsEnabled = false;
  searchBoxLabelsVisible = false;
  statusMessage = 'Please select data to load from the sidebar';
  isLoading = false;
  loadingMessage = 'Loading...';

  // Tax Return Type selection
  taxReturnTypes: Array<{ id: string; name: string }> = [
    { id: 'corporate', name: 'Corporate' },
    { id: 'individual', name: 'Individual' }
  ];
  selectedTaxReturnType = 'corporate';

  // Selection state
  years: string[] = [];
  documents: Array<{ id: string; name: string }> = [];
  languages: Array<{ id: string; name: string }> = [];
  pages: number[] = [];

  selectedYear = '';
  selectedDocument = '';
  selectedLanguage = '';
  selectedPage: number | null = null;
  hasOcrData = false;

  // Line item selection
  currentSelectionMode: 'line_item' | 'anchors' | 'value_search_box' | '' = '';
  isSelectionActive = false;
  lineItemSelections: LineItemSelections = {
    line_item_text: null,
    anchors: [],
    value_search_box: null
  };
  lineItemName = '';

  // Delete mode - selected line item for deletion
  selectedLineItemForDeletion: string | null = null;

  // Mouse and drawing state
  isDragging = false;
  lastMousePos = { x: 0, y: 0 };
  isDrawing = false;
  selectionStart = { x: 0, y: 0 };
  selectionEnd = { x: 0, y: 0 };
  currentSelectionRect: any = null;
  draggingSearchBox: HTMLElement | null = null;
  dragOffset = { x: 0, y: 0 };

  // Image loading state
  imageLoadAttempts = 0;

  constructor(
    private ocrApiService: OcrApiService,
    private elementRef: ElementRef
  ) { }

  ngOnInit(): void {
    this.initializeDropdowns();
  }

  ngOnDestroy(): void {
    // Clean up any resources
  }

  // ===== INITIALIZATION =====

  async initializeDropdowns(): Promise<void> {
    this.updateStatus('Scanning template folder...');
    this.showLoading('Scanning template folder...');

    this.ocrApiService.discoverStructure(this.selectedTaxReturnType).subscribe({
      next: (structure) => {
        this.hideLoading();
        if (structure && Object.keys(structure).length > 0) {
          this.templateStructure = structure;
          this.years = Object.keys(structure).sort();
          this.updateStatus(`Found ${this.years.length} years - ready to load data`);
        } else {
          this.updateStatus('No data found in template folder');
        }
      },
      error: (error) => {
        this.hideLoading();
        this.updateStatus('Error scanning template folder: ' + error.message);
        console.error('Error initializing dropdowns:', error);
      }
    });
  }

  onTaxReturnTypeChange(): void {
    // Reset all dependent dropdowns when tax return type changes
    this.years = [];
    this.documents = [];
    this.languages = [];
    this.pages = [];
    this.selectedYear = '';
    this.selectedDocument = '';
    this.selectedLanguage = '';
    this.selectedPage = null;
    this.hasOcrData = false;
    this.ocrData = null;
    this.currentImage = null;
    this.existingLineItems = [];
    this.templateStructure = {};

    // Reload structure for the new tax return type
    this.initializeDropdowns();
  }

  // ===== DROPDOWN HANDLERS =====

  onYearChange(): void {
    // Reset dependent dropdowns
    this.documents = [];
    this.languages = [];
    this.pages = [];
    this.selectedDocument = '';
    this.selectedLanguage = '';
    this.selectedPage = null;

    if (this.selectedYear) {
      this.updateStatus(`Loading documents for ${this.selectedYear}...`);
      this.documents = this.getDocuments(this.selectedYear);

      if (this.documents.length > 0) {
        this.updateStatus(`Found ${this.documents.length} document(s) for ${this.selectedYear}`);
      } else {
        this.updateStatus(`No documents found for ${this.selectedYear}`);
      }
    }
  }

  onDocumentChange(): void {
    // Reset dependent dropdowns
    this.languages = [];
    this.pages = [];
    this.selectedLanguage = '';
    this.selectedPage = null;

    if (this.selectedYear && this.selectedDocument) {
      this.updateStatus(`Loading languages for ${this.selectedDocument}...`);
      this.languages = this.getLanguages(this.selectedYear, this.selectedDocument);

      if (this.languages.length > 0) {
        this.updateStatus(`Found ${this.languages.length} language(s) for ${this.selectedDocument}`);
      } else {
        this.updateStatus(`No languages found for ${this.selectedDocument}`);
      }
    }
  }

  onLanguageChange(): void {
    // Reset page dropdown
    this.pages = [];
    this.selectedPage = null;

    if (this.selectedYear && this.selectedDocument && this.selectedLanguage) {
      this.updateStatus(`Loading pages for ${this.selectedDocument} (${this.selectedLanguage})...`);
      this.pages = this.getPages(this.selectedYear, this.selectedDocument, this.selectedLanguage);

      if (this.pages.length > 0) {
        this.updateStatus(`Found ${this.pages.length} page(s) for ${this.selectedDocument} (${this.selectedLanguage})`);
      } else {
        this.updateStatus(`No pages found for ${this.selectedDocument} (${this.selectedLanguage})`);
      }
    }
  }

  onPageChange(): void {
    if (this.selectedYear && this.selectedDocument && this.selectedLanguage && this.selectedPage !== null) {
      const ocrPages = this.getOCRPages(this.selectedYear, this.selectedDocument, this.selectedLanguage);
      // Ensure selectedPage is a number for comparison
      const pageNum = typeof this.selectedPage === 'string' ? parseInt(this.selectedPage, 10) : this.selectedPage;
      this.hasOcrData = ocrPages.includes(pageNum);

      if (this.hasOcrData) {
        this.updateStatus(`Ready to load: ${this.selectedYear} > ${this.selectedDocument} > ${this.selectedLanguage} > Page ${this.selectedPage}`);
      } else {
        this.updateStatus(`No OCR data found for Page ${this.selectedPage} - Generate OCR first to continue`);
      }
    } else {
      this.hasOcrData = false;
    }
  }

  // ===== DATA LOADING =====

  async loadSelectedData(): Promise<void> {
    if (!this.selectedYear || !this.selectedDocument || !this.selectedLanguage || !this.selectedPage) {
      this.updateStatus('Please select year, document, language, and page');
      return;
    }

    try {
      // Reset image load attempts for new data load
      this.imageLoadAttempts = 0;

      this.updateStatus('Loading OCR data...');
      this.showLoading('Loading OCR data...');

      // Load OCR data
      this.ocrApiService.loadOcrData(
        this.selectedYear,
        this.selectedLanguage,
        this.selectedDocument,
        this.selectedPage,
        this.selectedTaxReturnType
      ).subscribe({
        next: (data) => {
          this.ocrData = data;
          this.updateStatus('OCR data loaded');
          this.loadImageData();
        },
        error: (error) => {
          this.hideLoading();
          this.updateStatus('Error loading OCR data: ' + error.message);
          console.error('Error loading OCR data:', error);
        }
      });
    } catch (error: any) {
      this.hideLoading();
      this.updateStatus('Error: ' + error.message);
      console.error('Error loading data:', error);
    }
  }

  private loadImageData(): void {
    if (!this.selectedYear || !this.selectedDocument || !this.selectedLanguage || !this.selectedPage) return;

    this.updateStatus('Loading image...');
    this.showLoading('Loading page image...');

    this.ocrApiService.loadImage(
      this.selectedYear,
      this.selectedLanguage,
      this.selectedDocument,
      this.selectedPage,
      this.selectedTaxReturnType
    ).subscribe({
      next: (blob) => {
        this.currentImage = URL.createObjectURL(blob);
        this.imageLoadAttempts = 0; // Reset counter on success
        this.updateStatus('Image loaded successfully');
        this.finalizeDataLoad();
      },
      error: () => {
        // Image not found - try to generate it from PDF (only once)
        if (this.imageLoadAttempts === 0) {
          this.imageLoadAttempts++;
          this.updateStatus('Image not found - generating from PDF...');
          this.generateImageFromPdf();
        } else {
          // Already tried to generate, proceed without image
          this.updateStatus('Continuing without image');
          this.imageLoadAttempts = 0; // Reset for next load
          this.currentImage = null;
          this.finalizeDataLoad();
        }
      }
    });
  }

  private generateImageFromPdf(): void {
    if (!this.selectedYear || !this.selectedDocument || !this.selectedLanguage || !this.selectedPage) return;

    const pdfPath = `template/${this.selectedYear}/${this.selectedLanguage}/${this.selectedDocument}/${this.selectedDocument}.pdf`;
    const outputPath = `template/${this.selectedYear}/${this.selectedLanguage}/${this.selectedDocument}/images/Page${this.selectedPage}.png`;

    this.updateStatus('Generating image from PDF - please wait...');
    this.showLoading('Generating image from PDF...');

    this.ocrApiService.convertPdfToImage(
      this.selectedYear,
      this.selectedDocument,
      this.selectedLanguage,
      this.selectedPage,
      pdfPath,
      outputPath,
      this.selectedTaxReturnType
    ).subscribe({
      next: (result) => {
        if (result.success) {
          this.updateStatus('Image generated successfully! Loading...');
          // Try loading the image again
          setTimeout(() => {
            this.loadImageData();
          }, 500); // Small delay to ensure file is written
        } else {
          this.updateStatus('Could not generate image - continuing without image');
          console.error('Image generation failed:', result.error);
          this.imageLoadAttempts = 0; // Reset counter
          this.currentImage = null;
          this.finalizeDataLoad();
        }
      },
      error: (error) => {
        this.updateStatus('Could not generate image - continuing without image');
        console.error('Error generating image:', error);
        this.imageLoadAttempts = 0; // Reset counter
        this.currentImage = null;
        this.finalizeDataLoad();
      }
    });
  }

  private finalizeDataLoad(): void {
    if (!this.selectedYear || !this.selectedDocument || !this.selectedLanguage || !this.selectedPage) return;

    // Hide loading spinner
    this.hideLoading();

    // Load existing line items
    this.loadExistingLineItems();

    // Set box limit to show all boxes
    if (this.ocrData) {
      this.boxLimit = this.ocrData.boxes.length;
    }

    // Note: Transform will be updated when image loads via onImageLoad()
  }

  onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    this.imageWidth = img.naturalWidth;
    this.imageHeight = img.naturalHeight;

    console.log(`Image loaded: ${this.imageWidth} × ${this.imageHeight}`);

    // Update transform now that we have image dimensions
    this.updateTransform();
    this.updateZoom();

    this.updateStatus(`Data loaded successfully - ${this.ocrData?.boxes.length || 0} OCR boxes`);
  }

  private loadExistingLineItems(): void {
    if (!this.selectedYear || !this.selectedDocument || !this.selectedLanguage || !this.selectedPage) return;

    this.updateStatus('Loading existing line items...');

    this.ocrApiService.loadLineItems(
      this.selectedYear,
      this.selectedDocument,
      this.selectedLanguage,
      this.selectedPage,
      this.selectedTaxReturnType
    ).subscribe({
      next: (items) => {
        this.existingLineItems = items;
        if (items.length > 0) {
          this.updateStatus(`Loaded ${items.length} existing line item(s)`);
        } else {
          this.updateStatus('No existing line items found for this page');
        }
      },
      error: (error) => {
        console.error('Error loading existing line items:', error);
        this.existingLineItems = [];
      }
    });
  }

  // ===== HELPER FUNCTIONS FOR DROPDOWNS =====

  private getDocuments(year: string): Array<{ id: string; name: string }> {
    const documents: Array<{ id: string; name: string }> = [];
    if (!this.templateStructure[year]) return documents;

    const documentSet = new Set<string>();
    Object.values(this.templateStructure[year]).forEach(languageData => {
      Object.keys(languageData).forEach(doc => documentSet.add(doc));
    });

    documentSet.forEach(doc => {
      documents.push({
        id: doc,
        name: this.formatDocumentName(doc)
      });
    });

    return documents.sort((a, b) => a.name.localeCompare(b.name));
  }

  private getLanguages(year: string, document: string): Array<{ id: string; name: string }> {
    const languages: Array<{ id: string; name: string }> = [];
    if (!this.templateStructure[year]) return languages;

    Object.keys(this.templateStructure[year]).forEach(lang => {
      if (this.templateStructure[year][lang][document]) {
        languages.push({
          id: lang,
          name: lang === 'EN' ? 'English' : lang === 'ES' ? 'Español' : lang
        });
      }
    });

    return languages.sort((a, b) => a.id.localeCompare(b.id));
  }

  private getPages(year: string, document: string, language: string): number[] {
    if (!this.templateStructure[year] ||
      !this.templateStructure[year][language] ||
      !this.templateStructure[year][language][document]) {
      return [];
    }

    const pageData = this.templateStructure[year][language][document];

    if (typeof pageData === 'object' && 'available_pages' in pageData) {
      return pageData.available_pages;
    }

    return pageData as number[];
  }

  private getOCRPages(year: string, document: string, language: string): number[] {
    if (!this.templateStructure[year] ||
      !this.templateStructure[year][language] ||
      !this.templateStructure[year][language][document]) {
      return [];
    }

    const pageData = this.templateStructure[year][language][document];

    if (typeof pageData === 'object' && 'ocr_pages' in pageData) {
      return pageData.ocr_pages;
    }

    return pageData as number[];
  }

  private formatDocumentName(docId: string): string {
    return docId
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // ===== TRANSFORM AND COORDINATE FUNCTIONS =====

  updateTransform(): void {
    if (!this.imageWidth || !this.imageHeight) {
      console.log('Image dimensions not available yet');
      return;
    }

    if (this.ocrData && this.ocrData.page_size && this.imageWidth && this.imageHeight) {
      let ocrPageWidth = this.ocrData.page_size.width;
      let ocrPageHeight = this.ocrData.page_size.height;
      const rotationAngle = this.ocrData.rotation_angle || 0;

      if (Math.abs(rotationAngle) === 90 || Math.abs(rotationAngle) === 270) {
        [ocrPageWidth, ocrPageHeight] = [ocrPageHeight, ocrPageWidth];
      }

      const scaleX = this.imageWidth / ocrPageWidth;
      const scaleY = this.imageHeight / ocrPageHeight;
      const scale = Math.min(scaleX, scaleY);

      this.transform.scaleX = scale;
      this.transform.scaleY = scale;
      this.transform.offsetX = (this.imageWidth - ocrPageWidth * scale) / 2;
      this.transform.offsetY = (this.imageHeight - ocrPageHeight * scale) / 2;

      console.log(`Transform: scale=${scale.toFixed(3)}, offset=(${this.transform.offsetX.toFixed(1)}, ${this.transform.offsetY.toFixed(1)})`);
    }
  }

  transformCoordinates(box: OcrBox): TransformedCoordinates {
    const origX = box.ulc.x;
    const origY = box.ulc.y;
    const origWidth = box.urc.x - box.ulc.x;
    const origHeight = box.llc.y - box.ulc.y;

    const rotationAngle = (this.ocrData && this.ocrData.rotation_angle) ? this.ocrData.rotation_angle : 0;

    let transformedX = origX;
    let transformedY = origY;
    let transformedWidth = origWidth;
    let transformedHeight = origHeight;

    if (rotationAngle !== 0 && this.ocrData?.page_size) {
      const centerX = this.ocrData.page_size.width / 2;
      const centerY = this.ocrData.page_size.height / 2;

      const radians = (rotationAngle * Math.PI) / 180;
      const cos = Math.cos(radians);
      const sin = Math.sin(radians);

      const relativeX = origX - centerX;
      const relativeY = origY - centerY;

      transformedX = (relativeX * cos - relativeY * sin) + centerX;
      transformedY = (relativeX * sin + relativeY * cos) + centerY;

      if (Math.abs(rotationAngle) === 90 || Math.abs(rotationAngle) === 270) {
        transformedWidth = origHeight;
        transformedHeight = origWidth;
      }
    }

    return {
      x: (transformedX * this.transform.scaleX) + this.transform.offsetX,
      y: (transformedY * this.transform.scaleY) + this.transform.offsetY,
      width: transformedWidth * this.transform.scaleX,
      height: transformedHeight * this.transform.scaleY,
      rotation: rotationAngle
    };
  }

  // ===== ZOOM AND PAN =====

  updateZoom(): void {
    this.updateStatus(`Zoom: ${Math.round(this.currentZoom * 100)}%`);
  }

  resetPan(): void {
    this.panOffset.x = 0;
    this.panOffset.y = 0;
    this.updateZoom();
  }

  // ===== KEYBOARD SHORTCUTS =====

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    const activeElement = document.activeElement;
    const isTyping = activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.tagName === 'SELECT'
    );

    if (isTyping) return;

    if (event.key === 'f' || event.key === 'F') {
      this.floatingCoordsEnabled = !this.floatingCoordsEnabled;
      event.preventDefault();
    }

    if (event.key === '0') {
      this.currentZoom = 1.0;
      this.updateZoom();
      event.preventDefault();
    }

    if (event.key === 'r' || event.key === 'R') {
      this.resetPan();
      event.preventDefault();
    }

    // Delete key - delete selected line item
    if (event.key === 'Delete' || event.key === 'Backspace') {
      if (this.selectedLineItemForDeletion) {
        this.deleteSelectedLineItem();
        event.preventDefault();
      }
    }

    // Escape key - clear delete selection
    if (event.key === 'Escape') {
      if (this.selectedLineItemForDeletion) {
        this.clearDeleteSelection();
        event.preventDefault();
      }
    }
  }

  // ===== UTILITY FUNCTIONS =====

  updateStatus(message: string): void {
    this.statusMessage = message;
    console.log('Status:', message);
  }

  showLoading(message: string = 'Loading...'): void {
    this.loadingMessage = message;
    this.isLoading = true;
  }

  hideLoading(): void {
    this.isLoading = false;
  }

  toggleFloatingCoords(): void {
    this.floatingCoordsEnabled = !this.floatingCoordsEnabled;
  }

  toggleHoverTooltips(): void {
    this.hoverTooltipsEnabled = !this.hoverTooltipsEnabled;
  }

  toggleSearchBoxLabels(): void {
    this.searchBoxLabelsVisible = !this.searchBoxLabelsVisible;
  }

  // ===== MOUSE EVENT HANDLERS =====

  onMouseDown(event: MouseEvent): void {
    if (!this.isSelectionActive || !this.imageContainer) {
      return;
    }

    const rect = this.imageContainer.nativeElement.getBoundingClientRect();
    // Apply inverse transformation to get coordinates in pre-transform space
    const rawX = event.clientX - rect.left;
    const rawY = event.clientY - rect.top;

    this.selectionStart = {
      x: (rawX / this.currentZoom) - this.panOffset.x,
      y: (rawY / this.currentZoom) - this.panOffset.y
    };
    this.selectionEnd = { ...this.selectionStart };
    this.isDrawing = true;
    this.updateSelectionRect();

    console.log('Start drawing:', {
      mousePos: { x: event.clientX, y: event.clientY },
      rawPos: { x: rawX, y: rawY },
      transformedPos: this.selectionStart,
      currentZoom: this.currentZoom,
      panOffset: this.panOffset
    });

    event.preventDefault();
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDrawing || !this.imageContainer) {
      return;
    }

    const rect = this.imageContainer.nativeElement.getBoundingClientRect();
    const rawX = event.clientX - rect.left;
    const rawY = event.clientY - rect.top;

    // Apply inverse transformation
    this.selectionEnd = {
      x: (rawX / this.currentZoom) - this.panOffset.x,
      y: (rawY / this.currentZoom) - this.panOffset.y
    };
    this.updateSelectionRect();
  }

  onMouseUp(event: MouseEvent): void {
    if (!this.isDrawing || !this.imageContainer) {
      return;
    }

    this.isDrawing = false;
    const rect = this.imageContainer.nativeElement.getBoundingClientRect();
    const rawX = event.clientX - rect.left;
    const rawY = event.clientY - rect.top;

    this.selectionEnd = {
      x: (rawX / this.currentZoom) - this.panOffset.x,
      y: (rawY / this.currentZoom) - this.panOffset.y
    };

    // Convert selection to OCR coordinates and capture
    this.captureSelection();

    // Clear the visual rectangle
    this.currentSelectionRect = null;
  }

  onMouseLeave(event: MouseEvent): void {
    if (this.isDrawing) {
      // Treat as mouse up
      this.onMouseUp(event);
    }
  }

  private updateSelectionRect(): void {
    if (!this.imageContainer) return;

    const left = Math.min(this.selectionStart.x, this.selectionEnd.x);
    const top = Math.min(this.selectionStart.y, this.selectionEnd.y);
    const width = Math.abs(this.selectionEnd.x - this.selectionStart.x);
    const height = Math.abs(this.selectionEnd.y - this.selectionStart.y);

    this.currentSelectionRect = {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`
    };
  }

  private captureSelection(): void {
    if (!this.imageContainer || !this.imageWidth || !this.imageHeight || !this.ocrData) {
      console.error('Cannot capture selection: missing container or image dimensions');
      return;
    }

    // Get rectangle bounds in pre-transform coordinate space
    const containerLeft = Math.min(this.selectionStart.x, this.selectionEnd.x);
    const containerTop = Math.min(this.selectionStart.y, this.selectionEnd.y);
    const containerWidth = Math.abs(this.selectionEnd.x - this.selectionStart.x);
    const containerHeight = Math.abs(this.selectionEnd.y - this.selectionStart.y);

    console.log('Rectangle coordinates (pre-transform):', {
      left: containerLeft, top: containerTop, width: containerWidth, height: containerHeight
    });

    // Get image element to calculate scale
    const imageElement = this.imageContainer.nativeElement.querySelector('.base-image');
    if (!imageElement) {
      console.error('Image element not found');
      return;
    }

    const imageRect = imageElement.getBoundingClientRect();

    // Convert from pre-transform container coordinates to image pixel coordinates
    const scaleToImage = this.imageWidth / (imageRect.width / this.currentZoom);

    const imageLeft = containerLeft * scaleToImage;
    const imageTop = containerTop * scaleToImage;
    const imageRight = (containerLeft + containerWidth) * scaleToImage;
    const imageBottom = (containerTop + containerHeight) * scaleToImage;

    const selectionBounds = {
      left: imageLeft,
      top: imageTop,
      right: imageRight,
      bottom: imageBottom
    };

    console.log('Selection bounds (image pixel space):', selectionBounds);

    // Handle different selection modes
    if (this.currentSelectionMode === 'value_search_box') {
      // For value search box, convert to OCR coordinate space and store
      const ocrLeft = (imageLeft - this.transform.offsetX) / this.transform.scaleX;
      const ocrTop = (imageTop - this.transform.offsetY) / this.transform.scaleY;
      const ocrRight = (imageRight - this.transform.offsetX) / this.transform.scaleX;
      const ocrBottom = (imageBottom - this.transform.offsetY) / this.transform.scaleY;

      this.processSearchBoxSelection(ocrLeft, ocrTop, ocrRight, ocrBottom);
    } else {
      // For line item and anchors, select OCR boxes within bounds
      this.selectWordsInBounds(selectionBounds);
    }
  }

  private processSearchBoxSelection(left: number, top: number, right: number, bottom: number): void {
    const searchBox: SearchBox = {
      ulc: { x: Math.round(left), y: Math.round(top) },
      urc: { x: Math.round(right), y: Math.round(top) },
      llc: { x: Math.round(left), y: Math.round(bottom) },
      lrc: { x: Math.round(right), y: Math.round(bottom) }
    };

    this.lineItemSelections.value_search_box = searchBox;
    console.log('Value Search Box captured:', searchBox);
    this.updateStatus(`Value Search Box captured: (${left.toFixed(0)}, ${top.toFixed(0)}) to (${right.toFixed(0)}, ${bottom.toFixed(0)})`);
  }

  private selectWordsInBounds(bounds: { left: number; top: number; right: number; bottom: number }): void {
    if (!this.ocrData) return;

    const selectedBoxes: number[] = [];
    const selectedTexts: string[] = [];

    this.ocrData.boxes.forEach((box, index) => {
      const transformedCoords = this.transformCoordinates(box);

      const boxBounds = {
        left: transformedCoords.x,
        top: transformedCoords.y,
        right: transformedCoords.x + transformedCoords.width,
        bottom: transformedCoords.y + transformedCoords.height
      };

      // Check if box overlaps with selection bounds
      const overlaps = boxBounds.left < bounds.right && boxBounds.right > bounds.left &&
        boxBounds.top < bounds.bottom && boxBounds.bottom > bounds.top;

      if (overlaps) {
        selectedBoxes.push(index);
        selectedTexts.push(box.raw_text);
        console.log(`SELECTED: "${box.raw_text}" at index ${index}`);
      }
    });

    console.log(`Selection result: ${selectedBoxes.length} boxes found`);

    if (selectedBoxes.length > 0) {
      this.processSelectedWords(selectedBoxes, selectedTexts);
    } else {
      this.updateStatus('No words selected in the drawn area');
    }
  }

  private processSelectedWords(selectedBoxes: number[], selectedTexts: string[]): void {
    // Combine text from selected boxes
    const combinedText = this.smartJoinTexts(selectedTexts);

    // Create search box from selected OCR boxes bounds
    const searchBox = this.calculateBoundingBox(selectedBoxes);

    if (this.currentSelectionMode === 'line_item') {
      this.lineItemSelections.line_item_text = searchBox;
      this.lineItemText = combinedText; // Store text for display
      this.updateStatus(`Line Item text captured: "${combinedText}"`);

      // Highlight selected boxes temporarily
      this.highlightBoxes(selectedBoxes, combinedText);
    } else if (this.currentSelectionMode === 'anchors') {
      this.lineItemSelections.anchors.push(searchBox);
      this.anchorTexts.push(combinedText); // Store text for display
      this.updateStatus(`Anchor ${this.lineItemSelections.anchors.length} added: "${combinedText}"`);

      // Highlight selected boxes temporarily
      this.highlightBoxes(selectedBoxes, combinedText);
    }

    console.log('Processed selection:', {
      mode: this.currentSelectionMode,
      text: combinedText,
      searchBox,
      boxIndices: selectedBoxes
    });
  }

  private smartJoinTexts(texts: string[]): string {
    if (texts.length === 0) return '';
    if (texts.length === 1) return texts[0];

    return texts.join(' ');
  }

  private calculateBoundingBox(boxIndices: number[]): SearchBox {
    if (!this.ocrData || boxIndices.length === 0) {
      return { ulc: { x: 0, y: 0 }, urc: { x: 0, y: 0 }, llc: { x: 0, y: 0 }, lrc: { x: 0, y: 0 } };
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    boxIndices.forEach(index => {
      const box = this.ocrData!.boxes[index];
      minX = Math.min(minX, box.ulc.x, box.llc.x);
      minY = Math.min(minY, box.ulc.y, box.urc.y);
      maxX = Math.max(maxX, box.urc.x, box.lrc.x);
      maxY = Math.max(maxY, box.llc.y, box.lrc.y);
    });

    return {
      ulc: { x: Math.round(minX), y: Math.round(minY) },
      urc: { x: Math.round(maxX), y: Math.round(minY) },
      llc: { x: Math.round(minX), y: Math.round(maxY) },
      lrc: { x: Math.round(maxX), y: Math.round(maxY) }
    };
  }

  selectedBoxIndices: number[] = [];
  lineItemBoxIndices: number[] = [];
  anchorBoxIndices: number[][] = [];

  // Store extracted text for display
  lineItemText = '';
  anchorTexts: string[] = [];

  private highlightBoxes(boxIndices: number[], text: string): void {
    // Store selected boxes for rendering with temporary green highlight
    this.selectedBoxIndices = boxIndices;

    // Also store permanently based on mode
    if (this.currentSelectionMode === 'line_item') {
      this.lineItemBoxIndices = [...boxIndices];
    } else if (this.currentSelectionMode === 'anchors') {
      this.anchorBoxIndices.push([...boxIndices]);
    }

    // Clear temporary green highlight after 2 seconds
    setTimeout(() => {
      this.selectedBoxIndices = [];
    }, 2000);
  }

  // ===== SELECTION MODE =====

  onSelectionModeChange(): void {
    // Handle selection mode changes
    if (!this.currentSelectionMode) {
      if (this.isSelectionActive) {
        this.toggleSelection();
      }
    }
  }

  toggleSelection(): void {
    this.isSelectionActive = !this.isSelectionActive;

    if (this.isSelectionActive) {
      this.updateStatus(`Selection mode active: ${this.currentSelectionMode.replace('_', ' ')} - Draw rectangle to select`);
    } else {
      this.updateStatus('Selection mode disabled');
    }
  }

  clearSelections(): void {
    this.lineItemSelections = {
      line_item_text: null,
      anchors: [],
      value_search_box: null
    };
    this.lineItemName = '';
    this.selectedBoxIndices = [];
    this.lineItemBoxIndices = [];
    this.anchorBoxIndices = [];
    this.lineItemText = '';
    this.anchorTexts = [];
    this.updateStatus('All selections cleared');
  }

  // ===== SAVE/DELETE LINE ITEMS =====

  async saveLineItem(): Promise<void> {
    if (!this.selectedYear || !this.selectedDocument || !this.selectedLanguage || !this.selectedPage) {
      alert('Please select year, document, language, and page first');
      return;
    }

    if (!this.lineItemSelections.value_search_box) {
      alert('Please select Value Search Box before saving');
      return;
    }

    const finalLineItemName = this.lineItemName || this.generateDefaultLineItemName();

    const lineItemData = {
      line_item_text: this.lineItemText || null,
      anchors: this.lineItemSelections.anchors,
      search_box: this.lineItemSelections.value_search_box,
      page_number: this.selectedPage
    };

    try {
      this.updateStatus('Saving line item data...');
      this.showLoading('Saving line item...');

      this.ocrApiService.saveLineItem(
        this.selectedYear,
        this.selectedDocument,
        this.selectedLanguage,
        lineItemData,
        finalLineItemName,
        this.selectedTaxReturnType
      ).subscribe({
        next: () => {
          this.hideLoading();
          this.updateStatus('Line item saved successfully');
          this.loadExistingLineItems();
          this.clearSelections();
        },
        error: (error) => {
          this.hideLoading();
          this.updateStatus('Error saving line item: ' + error.message);
          alert('Error: ' + error.message);
        }
      });
    } catch (error: any) {
      this.hideLoading();
      this.updateStatus('Error saving line item: ' + error.message);
      console.error('Save error:', error);
      alert('Error saving line item: ' + error.message);
    }
  }

  private generateDefaultLineItemName(): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `line_item_${result}`;
  }

  // ===== DELETE LINE ITEM =====

  onSearchBoxClick(item: LineItem, event: Event): void {
    event.stopPropagation();

    // Toggle selection - if already selected, deselect; otherwise select
    if (this.selectedLineItemForDeletion === item.id) {
      this.selectedLineItemForDeletion = null;
      this.updateStatus('Deselected line item');
    } else {
      this.selectedLineItemForDeletion = item.id || null;
      this.updateStatus(`Selected "${item.id}" for deletion - click again to deselect or press Delete`);
    }
  }

  async deleteSelectedLineItem(): Promise<void> {
    if (!this.selectedLineItemForDeletion) {
      alert('Please click on a search box to select it for deletion first');
      return;
    }

    if (!this.selectedYear || !this.selectedDocument || !this.selectedLanguage || !this.selectedPage) {
      alert('Please ensure year, document, language, and page are selected');
      return;
    }

    const lineItemId = this.selectedLineItemForDeletion;

    if (!confirm(`Are you sure you want to delete line item "${lineItemId}"? This action cannot be undone.`)) {
      return;
    }

    try {
      this.updateStatus(`Deleting line item "${lineItemId}"...`);
      this.showLoading('Deleting line item...');

      this.ocrApiService.deleteLineItem(
        this.selectedYear,
        this.selectedDocument,
        this.selectedLanguage,
        this.selectedPage,
        lineItemId,
        this.selectedTaxReturnType
      ).subscribe({
        next: (result) => {
          this.hideLoading();
          if (result.success) {
            this.updateStatus(`Line item "${lineItemId}" deleted successfully`);
            this.selectedLineItemForDeletion = null;
            this.loadExistingLineItems(); // Refresh the list
          } else {
            throw new Error(result.error || 'Unknown error');
          }
        },
        error: (error) => {
          this.hideLoading();
          this.updateStatus(`Error deleting line item: ${error.message}`);
          alert('Error: ' + error.message);
        }
      });
    } catch (error: any) {
      this.hideLoading();
      this.updateStatus(`Error deleting line item: ${error.message}`);
      console.error('Delete error:', error);
      alert('Error deleting line item: ' + error.message);
    }
  }

  clearDeleteSelection(): void {
    this.selectedLineItemForDeletion = null;
    this.updateStatus('Cleared delete selection');
  }

  // ===== API ACTIONS =====

  async generateOCR(): Promise<void> {
    if (!this.selectedYear || !this.selectedDocument || !this.selectedLanguage || !this.selectedPage) {
      alert('Please select year, document, language, and page first');
      return;
    }

    if (!confirm(`Generate OCR data for ${this.selectedYear}/${this.selectedDocument}/${this.selectedLanguage}/Page ${this.selectedPage}?`)) {
      return;
    }

    try {
      this.updateStatus('Generating OCR data - this may take 10-30 seconds...');
      this.showLoading('Generating OCR data - this may take 10-30 seconds...');

      this.ocrApiService.generateOCR(
        this.selectedYear,
        this.selectedDocument,
        this.selectedLanguage,
        this.selectedPage,
        this.selectedTaxReturnType
      ).subscribe({
        next: (result) => {
          if (result.success) {
            this.updateStatus('OCR data generated successfully! You can now load the data.');

            // Refresh the structure to update available OCR pages
            this.ocrApiService.discoverStructure(this.selectedTaxReturnType).subscribe({
              next: (structure) => {
                this.hideLoading();
                this.templateStructure = structure;

                // Update hasOcrData flag - ensure page number comparison works correctly
                const ocrPages = this.getOCRPages(this.selectedYear, this.selectedDocument, this.selectedLanguage);
                const pageNum = typeof this.selectedPage === 'string' ? parseInt(this.selectedPage, 10) : this.selectedPage;
                this.hasOcrData = ocrPages.includes(pageNum!);

                if (this.hasOcrData) {
                  this.updateStatus('OCR data generated successfully! Click "Load Selected Data" to continue.');
                }
              },
              error: (error) => {
                this.hideLoading();
                console.error('Error refreshing structure:', error);
                this.hasOcrData = true; // Assume success even if refresh fails
                this.updateStatus('OCR data generated successfully! Click "Load Selected Data" to continue.');
              }
            });
          } else {
            this.hideLoading();
            throw new Error(result.error || 'Unknown error');
          }
        },
        error: (error) => {
          this.hideLoading();
          this.updateStatus('Error generating OCR: ' + error.message);
          alert('Error generating OCR: ' + error.message);
        }
      });
    } catch (error: any) {
      this.hideLoading();
      this.updateStatus('Error generating OCR: ' + error.message);
      console.error('OCR generation error:', error);
      alert('Error generating OCR: ' + error.message);
    }
  }



  exportAlignedData(): void {
    if (!this.ocrData) return;

    const alignedData = {
      ...this.ocrData,
      transform: this.transform,
      imageInfo: {
        width: this.imageWidth,
        height: this.imageHeight
      },
      rotationApplied: this.ocrData.rotation_angle || 0,
      alignedBoxes: this.ocrData.boxes.map(box => ({
        ...box,
        transformed: this.transformCoordinates(box)
      }))
    };

    const blob = new Blob([JSON.stringify(alignedData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'aligned_ocr_data.json';
    a.click();
    URL.revokeObjectURL(url);

    this.updateStatus('Aligned data exported successfully');
  }

  // ===== HELPER METHODS FOR TEMPLATE =====

  getBoxStyle(box: OcrBox): any {
    const transformed = this.transformCoordinates(box);

    if (!this.imageWidth || !this.imageHeight) {
      return {};
    }

    const left = (transformed.x / this.imageWidth) * 100;
    const top = (transformed.y / this.imageHeight) * 100;
    const width = (transformed.width / this.imageWidth) * 100;
    const height = (transformed.height / this.imageHeight) * 100;

    const style: any = {
      left: `${left}%`,
      top: `${top}%`,
      width: `${width}%`,
      height: `${height}%`
    };

    if (transformed.rotation && transformed.rotation !== 0) {
      style.transform = `rotate(${transformed.rotation}deg)`;
      style['transform-origin'] = 'center center';
    }

    return style;
  }

  getSearchBoxStyle(item: LineItem): any {
    if (!item.search_box || !this.imageWidth || !this.imageHeight) {
      return {};
    }

    const searchBox = item.search_box;

    // Transform search box coordinates
    const leftX = (searchBox.ulc.x * this.transform.scaleX) + this.transform.offsetX;
    const topY = (searchBox.ulc.y * this.transform.scaleY) + this.transform.offsetY;
    const rightX = (searchBox.lrc.x * this.transform.scaleX) + this.transform.offsetX;
    const bottomY = (searchBox.lrc.y * this.transform.scaleY) + this.transform.offsetY;

    // Calculate position as percentages
    const left = (leftX / this.imageWidth) * 100;
    const top = (topY / this.imageHeight) * 100;
    const width = ((rightX - leftX) / this.imageWidth) * 100;
    const height = ((bottomY - topY) / this.imageHeight) * 100;

    return {
      left: `${left}%`,
      top: `${top}%`,
      width: `${width}%`,
      height: `${height}%`
    };
  }

  getCurrentSelectionBoxStyle(searchBox: SearchBox): any {
    if (!searchBox || !this.imageWidth || !this.imageHeight) {
      return {};
    }

    // Transform search box coordinates (same as getSearchBoxStyle)
    const leftX = (searchBox.ulc.x * this.transform.scaleX) + this.transform.offsetX;
    const topY = (searchBox.ulc.y * this.transform.scaleY) + this.transform.offsetY;
    const rightX = (searchBox.lrc.x * this.transform.scaleX) + this.transform.offsetX;
    const bottomY = (searchBox.lrc.y * this.transform.scaleY) + this.transform.offsetY;

    // Calculate position as percentages
    const left = (leftX / this.imageWidth) * 100;
    const top = (topY / this.imageHeight) * 100;
    const width = ((rightX - leftX) / this.imageWidth) * 100;
    const height = ((bottomY - topY) / this.imageHeight) * 100;

    return {
      left: `${left}%`,
      top: `${top}%`,
      width: `${width}%`,
      height: `${height}%`
    };
  }

  isInAnyAnchor(boxIndex: number): boolean {
    return this.anchorBoxIndices.some(anchorIndices => anchorIndices.includes(boxIndex));
  }
}

